import { generateErrorId, extractOriginalMessage } from './errorHandlerBase';
import store from '../index';
import { addCentralizedError, showErrorModal, StandUpError, STANDUP_API_NAMES } from '../slices/standUpSlice';

/**
 * Utility function to handle StandUp API errors in a centralized way
 * This function creates a StandUpError object and adds it to the centralized error state
 * It also optionally shows the error modal if displayModal is true
 * 
 * @param apiName - The name of the API that failed (use STANDUP_API_NAMES constants)
 * @param error - The error object or string received from the API
 * @param displayModal - Whether to show the error modal immediately (default: true)
 * @param context - Additional context information for debugging
 * @returns The created StandUpError object with a unique ID
 */
export const handleStandUpError = (
  apiName: string,
  error: any,
  displayModal: boolean = true,
  context?: any
): StandUpError => {
  // Extract a normalized error message
  const errorMessage = extractOriginalMessage(error) || `An error occurred in ${apiName}`;

  // Create the StandUpError object
  const standUpError: StandUpError = {
    id: generateErrorId(),
    apiName,
    message: errorMessage,
    timestamp: Date.now(),
    context: context || {
      originalError: error,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown'
    }
  };

  // Add to centralized error state
  store.dispatch(addCentralizedError(standUpError));

  // Optionally show the error modal
  if (displayModal) {
    store.dispatch(showErrorModal(standUpError));
  }

  // Log to console for development
  console.error(`[StandUp Error] ${apiName}:`, {
    message: errorMessage,
    errorId: standUpError.id,
    originalError: error,
    context: standUpError.context
  });

  return standUpError;
};

/**
 * Wrapper function for async thunks that handles errors gracefully
 * This prevents promise rejections that would bubble up to ErrorBoundary
 * 
 * @param apiCall - The API function to call
 * @param apiName - The name of the API (use STANDUP_API_NAMES constants)
 * @param displayModal - Whether to show error modal on failure
 * @param context - Additional context for error tracking
 * @returns Promise that always resolves, either with data or null
 */
export const safeApiCall = async <T>(
  apiCall: () => Promise<T>,
  apiName: string,
  displayModal: boolean = true,
  context?: any
): Promise<T | null> => {
  try {
    const result = await apiCall();
    return result;
  } catch (error) {
    handleStandUpError(apiName, error, displayModal, context);
    // Return null instead of throwing to prevent ErrorBoundary trigger
    return null;
  }
};

/**
 * Helper function to create user-friendly error messages based on API name
 * This provides consistent messaging across the application
 * 
 * @param apiName - The name of the API that failed
 * @param originalMessage - The original error message from the API
 * @returns A user-friendly error message
 */
export const getUserFriendlyErrorMessage = (apiName: string, originalMessage: string): string => {
  const friendlyMessages: Record<string, string> = {
    [STANDUP_API_NAMES.FETCH_STANDUP_DATA]: 'Unable to load standup data. Please try again.',
    [STANDUP_API_NAMES.FETCH_TOP_OPPORTUNITIES]: 'Unable to load top opportunities. Please try again.',
    [STANDUP_API_NAMES.FETCH_NEWS]: 'Unable to load news. Please try again.',
    [STANDUP_API_NAMES.FETCH_NEWS_DETAILS]: 'Unable to load news details. Please try again.',
    [STANDUP_API_NAMES.ADD_NEWS]: 'Failed to save news. Please try again',
    [STANDUP_API_NAMES.DELETE_NEWS]: 'Unable to delete news. Please try again.',
    [STANDUP_API_NAMES.FETCH_FAVORITES]: 'Unable to load favorites. Please try again.',
    [STANDUP_API_NAMES.FETCH_LIGHTHOUSE_EXECUTION]: 'Unable to load lighthouse execution data. Please try again.',
    [STANDUP_API_NAMES.FETCH_METRIC_TREND]: 'Unable to load metric trend data. Please try again.',
    [STANDUP_API_NAMES.FETCH_KPI_METRICS]: 'Unable to load KPI metrics. Please try again.',
    [STANDUP_API_NAMES.FETCH_LIGHTHOUSE_TASKS]: 'Unable to load lighthouse tasks. Please try again.',
    [STANDUP_API_NAMES.ADD_FAVORITE]: 'Unable to add favorite. Please try again.',
    [STANDUP_API_NAMES.UPDATE_FAVORITE]: 'Unable to update favorite. Please try again.',
    [STANDUP_API_NAMES.REMOVE_FAVORITE]: 'Unable to remove favorite. Please try again.',
    [STANDUP_API_NAMES.SUBMIT_STANDUP]: 'Unable to submit standup. Please try again.',
    [STANDUP_API_NAMES.UPDATE_STANDUP]: 'Unable to update standup. Please try again.',
    [STANDUP_API_NAMES.CREATE_ACTION_PLAN]: 'Unable to create action plan. Please try again.',
    [STANDUP_API_NAMES.UPDATE_ACTION_PLAN]: 'Unable to update action plan. Please try again.',
    [STANDUP_API_NAMES.FETCH_ACTION_PLAN]: 'Unable to load action plan details. Please try again.',
    [STANDUP_API_NAMES.ACCEPT_ACTION_PLAN]: 'Unable to accept action plan. Please try again.',
    [STANDUP_API_NAMES.DECLINE_ACTION_PLAN]: 'Unable to decline action plan. Please try again.',
    [STANDUP_API_NAMES.COMPLETE_ACTION_PLAN]: 'Unable to complete action plan. Please try again.',
    [STANDUP_API_NAMES.DELETE_ACTION_PLAN]: 'Unable to delete action plan. Please try again.',
    [STANDUP_API_NAMES.TOGGLE_FAVORITE_OPPORTUNITY]: 'Unable to toggle favorite opportunity. Please try again.',
    [STANDUP_API_NAMES.DISCARD_STANDUP]: 'Unable to discard standup. Please try again.',
    [STANDUP_API_NAMES.UPDATE_ACTION_PLAN_TOP_OPPORTUNITY]: 'Unable to update action plan. Please try again.',
    [STANDUP_API_NAMES.DOWNLOAD_ACTION_PLAN_PDF]: 'Unable to download action plan PDF. Please try again.',
  };

  return friendlyMessages[apiName] || `Unable to complete the action. ${originalMessage || 'Please try again.'}`;
};

/**
 * Enhanced error handler that creates user-friendly messages
 * and handles both display and logging
 * 
 * @param apiName - The name of the API that failed
 * @param error - The error object or string
 * @param displayModal - Whether to show error modal
 * @param context - Additional context
 * @returns The created StandUpError object
 */
export const handleStandUpErrorWithFriendlyMessage = (
  apiName: string,
  error: any,
  displayModal: boolean = true,
  context?: any
): StandUpError => {
  // Get the original error message
  let originalMessage: string;
  
  if (typeof error === 'string') {
    originalMessage = error;
  } else if (error?.payload?.error) {
    originalMessage = error.payload.error;
  } else if (error?.payload?.message) {
    originalMessage = error.payload.message;
  } else if (error?.response?.data?.message) {
    originalMessage = error.response.data.message;
  } else if (error?.message) {
    originalMessage = error.message;
  } else {
    originalMessage = 'An unknown error occurred';
  }

  // Get user-friendly message
  const friendlyMessage = getUserFriendlyErrorMessage(apiName, originalMessage);

  // Create enhanced error object
  const standUpError: StandUpError = {
    id: generateErrorId(),
    apiName,
    message: friendlyMessage,
    timestamp: Date.now(),
    context: {
      originalMessage,
      ...context,
      originalError: error,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown'
    }
  };

  // Add to centralized error state
  store.dispatch(addCentralizedError(standUpError));

  // Optionally show the error modal
  if (displayModal) {
    store.dispatch(showErrorModal(standUpError));
  }

  // Log to console for development
  console.error(`[StandUp Error] ${apiName}:`, {
    userFriendlyMessage: friendlyMessage,
    originalMessage: originalMessage,
    errorId: standUpError.id,
    originalError: error,
    context: standUpError.context
  });

  return standUpError;
};

/**
 * Type guard to check if a thunk action was fulfilled
 * This helps with type safety when checking thunk results
 */
export const isThunkFulfilled = (action: any): boolean => {
  return action.type && action.type.endsWith('/fulfilled');
};

/**
 * Type guard to check if a thunk action was rejected
 * This helps with type safety when checking thunk results
 */
export const isThunkRejected = (action: any): boolean => {
  return action.type && action.type.endsWith('/rejected');
};
